
import { ArrowLeft, Shield, Heart, Users, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import AdPanel from '@/components/ads/AdPanel';
import LegalFooter from '@/components/common/LegalFooter';

const TermsOfService = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-carbon">
      <AdPanel />
      
      <div className="p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center mb-6">
            <Button
              onClick={handleBack}
              variant="ghost"
              size="sm"
              className="mr-3 text-ice-white hover:bg-ice-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center">
              <img 
                src="/lovable-uploads/8c71c5e9-9b7a-44a8-8098-652cabdedf5f.png" 
                alt="ALPHA Logo" 
                className="w-8 h-8 mr-3"
              />
              <h1 className="text-2xl font-bold text-ice-white">Terms of Service</h1>
            </div>
          </div>

          <Card className="bg-carbon border-ice-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-ice-white">Last Updated: June 24, 2025</CardTitle>
            </CardHeader>
            <CardContent className="text-ice-white/80 space-y-4">
              <p>
                Welcome to ALPHA, a dating platform designed to connect LGBTQ+ individuals in a safe, 
                inclusive environment. By using our service, you agree to these Terms of Service.
              </p>
              <div className="flex items-start space-x-3 p-4 bg-neon-red/10 rounded-lg border border-neon-red/20">
                <AlertTriangle className="w-5 h-5 text-neon-red flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-ice-white mb-1">Important Notice</p>
                  <p>You must be 18 years or older to use this service. This platform contains adult content and is intended for mature audiences only.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-ice-white">
                  <Users className="w-5 h-5 text-neon-red" />
                  1. Account Eligibility and Registration
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>Age Requirement:</strong> You must be at least 18 years old to create an account.</p>
                <p><strong>Identity:</strong> You agree to provide accurate information about yourself and maintain only one account.</p>
                <p><strong>Verification:</strong> We may require age verification and reserve the right to verify your identity.</p>
                <p><strong>Account Security:</strong> You are responsible for maintaining the security of your account credentials.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-ice-white">
                  <Heart className="w-5 h-5 text-neon-red" />
                  2. Community Guidelines and User Conduct
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>Respectful Behavior:</strong> Treat all users with respect, regardless of their identity, orientation, or background.</p>
                <p><strong>No Discrimination:</strong> Harassment, hate speech, or discrimination based on sexual orientation, gender identity, race, religion, or any other characteristic is strictly prohibited.</p>
                <p><strong>Authentic Profiles:</strong> Use recent, authentic photos of yourself. Fake profiles or catfishing is not allowed.</p>
                <p><strong>Appropriate Content:</strong> Do not share explicit sexual content, nude photos, or inappropriate material.</p>
                <p><strong>Privacy Respect:</strong> Do not share other users' personal information without consent.</p>
                <p><strong>Legal Compliance:</strong> Follow all applicable laws and regulations.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-ice-white">
                  <Shield className="w-5 h-5 text-neon-red" />
                  3. Content and Safety
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>Content Moderation:</strong> All photos and content are subject to automated and manual review for safety and compliance.</p>
                <p><strong>Reporting:</strong> Users can report inappropriate behavior or content. We investigate all reports promptly.</p>
                <p><strong>Blocking:</strong> You can block users to prevent them from contacting you or viewing your profile.</p>
                <p><strong>Safety Features:</strong> We provide tools to help you stay safe, including location sharing controls and verification badges.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-ice-white">
                  <Users className="w-5 h-5 text-neon-red" />
                  4. Matching and Communication
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>Matching Algorithm:</strong> Our system uses your preferences, location, and behavior to suggest compatible matches.</p>
                <p><strong>Messaging:</strong> You can only message users you've matched with or who have initiated contact with you.</p>
                <p><strong>Message Storage:</strong> Messages are stored securely and may be reviewed for safety purposes.</p>
                <p><strong>No Guarantees:</strong> We do not guarantee that you will find matches or romantic connections.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-ice-white">
                  <AlertTriangle className="w-5 h-5 text-neon-red" />
                  5. Prohibited Activities
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p>The following activities are strictly prohibited:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Creating fake profiles or impersonating others</li>
                  <li>Using the service for commercial purposes without permission</li>
                  <li>Attempting to hack, disrupt, or damage our systems</li>
                  <li>Sharing explicit sexual content or soliciting sexual services</li>
                  <li>Promoting illegal activities or substances</li>
                  <li>Spam messaging or automated behavior</li>
                  <li>Collecting user data for external use</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">6. Privacy and Data</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p>Your privacy is important to us. Please review our <a href="/privacy-policy" className="text-neon-red hover:underline">Privacy Policy</a> to understand how we collect, use, and protect your information.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">7. Subscription and Payments</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>Free Service:</strong> Basic features are available for free with limitations.</p>
                <p><strong>Premium Features:</strong> Additional features may be available through paid subscriptions.</p>
                <p><strong>Billing:</strong> Subscription fees are charged in advance and are non-refundable except as required by law.</p>
                <p><strong>Cancellation:</strong> You may cancel your subscription at any time through your account settings.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">8. Enforcement and Termination</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>Violations:</strong> We may warn, suspend, or permanently ban accounts that violate these terms.</p>
                <p><strong>Appeal Process:</strong> You may appeal account actions by contacting our support team.</p>
                <p><strong>Service Termination:</strong> We reserve the right to modify or discontinue the service at any time.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">9. Limitation of Liability</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p>ALPHA provides this service "as is" without warranties. We are not responsible for:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Actions or behavior of other users</li>
                  <li>Meetings or interactions that occur offline</li>
                  <li>Loss of data or service interruptions</li>
                  <li>Indirect or consequential damages</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">10. Dispute Resolution</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p><strong>Contact First:</strong> Please contact our support team to resolve any disputes before taking legal action.</p>
                <p><strong>Governing Law:</strong> These terms are governed by the laws of Germany.</p>
                <p><strong>Jurisdiction:</strong> Any legal disputes will be resolved in the courts of Berlin, Germany.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">11. Changes to Terms</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p>We may update these terms periodically. We will notify users of significant changes through the app or email. Continued use of the service constitutes acceptance of updated terms.</p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">12. Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p>For questions about these Terms of Service, please contact us:</p>
                <p><strong>Email:</strong> support@alphamanonly.com</p>
                <p><strong>Address:</strong> Kfir Yehuda, Fuggerstraße 9, 10777 Berlin</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center py-8">
            <p className="text-ice-white/60 text-sm">
              By using ALPHA, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>

          <LegalFooter />
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
